/**
 * Wrapper for the `video` learning block (F3-F). Plays the referenced
 * `video_asset` and WIRES viewing to the block's completion:
 * emits `{ watchedPct }` as the student's answer via `onChange`; the
 * `learning-blocks` engine (`video` block, F2-B) marks it complete once it crosses
 * `minWatchPct`. The player already persists `video_watch_progress` local-first.
 */
import { type Component, createMemo, createSignal, Show, untrack } from "solid-js";
import { msg } from "@lingui/core/macro";
import { CheckCircle2, Film } from "@fleet/icons";
import { DEFAULT_MIN_WATCH_PCT, isWatchComplete, type VideoDef } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { VideoPlayer } from "./video-player";

export interface VideoPlayerBlockProps {
  def: VideoDef;
  value: unknown;
  onChange: (response: unknown) => void;
  locked?: boolean;
}

function readWatchedPct(value: unknown): number {
  if (value && typeof value === "object" && "watchedPct" in value) {
    const v = (value as { watchedPct: unknown }).watchedPct;
    return typeof v === "number" ? v : 0;
  }
  return 0;
}

export const VideoPlayerBlock: Component<VideoPlayerBlockProps> = (props) => {
  const { t } = useI18n();
  const minWatch = () => props.def.minWatchPct ?? DEFAULT_MIN_WATCH_PCT;
  const [livePct, setLivePct] = createSignal(untrack(() => readWatchedPct(props.value)));
  const complete = createMemo(() => isWatchComplete(livePct(), minWatch()));

  return (
    <div class="space-y-3" data-testid="video-block">
      <Show
        when={props.def.videoAssetId}
        fallback={
          <div class="flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-border text-muted-foreground">
            <Film class="h-8 w-8" />
            <p class="text-sm">{t(msg`No video selected yet.`)}</p>
          </div>
        }
      >
        <VideoPlayer
          videoId={props.def.videoAssetId}
          minWatchPct={minWatch()}
          onWatchedPctChange={(pct) => {
            setLivePct(pct);
            // Emits the student's answer; the engine evaluates completion.
            if (!props.locked) {
              props.onChange({ watchedPct: pct });
            }
          }}
        />
      </Show>

      <div
        class="flex items-center gap-2 text-sm"
        classList={{
          "text-success": complete(),
          "text-muted-foreground": !complete(),
        }}
        data-testid="video-block-status"
        data-complete={complete() ? "1" : "0"}
      >
        <Show
          when={complete()}
          fallback={
            <span>
              {t(msg`Watch at least {pct}% to complete`, {
                pct: Math.round(minWatch() * 100),
              })}
            </span>
          }
        >
          <CheckCircle2 class="h-4 w-4" />
          <span>{t(msg`Video completed`)}</span>
        </Show>
      </div>
    </div>
  );
};
